import { useRef, useState } from 'react';
import {
  AlertCircle,
  Download,
  FileImage,
  FileSignature,
  FileText,
  File as FileIcon,
  Loader2,
  Plus,
  Wrench,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  getSignedDownloadUrl,
  useCustomerFiles,
  useUploadCustomerFile,
} from '@/hooks/useCustomerFiles';
import type { CustomerFile, CustomerFileKind } from '@/types/domain';

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB — matches the Storage bucket cap.

interface FileLibraryProps {
  customerId: string;
}

type UploadableKind = Extract<CustomerFileKind, 'contract' | 'service_doc' | 'other'>;

const KIND_OPTIONS: ReadonlyArray<{ value: UploadableKind; label: string }> = [
  { value: 'contract', label: 'Contract' },
  { value: 'service_doc', label: 'Service doc' },
  { value: 'other', label: 'Other' },
];

/**
 * FileLibrary — list + upload the current customer's files.
 *
 * Lists from orders.customer_files (RLS-scoped, customer-visible only).
 * Uploads to Storage bucket brix-order-files under `${customerId}/...`.
 *
 * Kept intentionally simple: pick a kind, choose a file, it lands in the
 * list. Notes / re-tagging is a future enhancement once we know the
 * real-world upload patterns.
 */
export function FileLibrary({ customerId }: FileLibraryProps) {
  const { data: files = [], isLoading, error: listError } = useCustomerFiles();
  const { mutate: upload, isPending: uploading, error: uploadError } = useUploadCustomerFile();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [kind, setKind] = useState<UploadableKind>('contract');
  const [localError, setLocalError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  function pickFile() {
    setLocalError(null);
    inputRef.current?.click();
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice still fires onChange.
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setLocalError(
        `That file is ${formatBytes(file.size)}, over the 50 MB limit. ` +
          'For larger files, email them to support and we will attach them on your account.',
      );
      return;
    }
    upload({ customerId, file, kind });
  }

  async function onDownload(f: CustomerFile) {
    setDownloadingId(f.id);
    try {
      const url = await getSignedDownloadUrl(f.storagePath);
      // Use a temp anchor with download attribute so the browser saves
      // with the original filename instead of the storage path.
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = f.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setLocalError('Could not generate a download link. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  }

  const showEmpty = !isLoading && files.length === 0 && !uploading;
  const errMessage =
    localError ??
    (uploadError instanceof Error ? uploadError.message : null) ??
    (listError instanceof Error ? listError.message : null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] tracking-tight text-brix-muted font-semibold mr-1">
          Kind
        </span>
        {KIND_OPTIONS.map((opt) => {
          const selected = kind === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setKind(opt.value)}
              className={cn(
                'text-xs px-2.5 py-1 rounded-full border transition-colors',
                selected
                  ? 'bg-brix-primary text-white border-brix-primary'
                  : 'bg-brix-surface text-brix-ink border-brix-border hover:bg-brix-surface-alt',
              )}
            >
              {opt.label}
            </button>
          );
        })}
        <div className="flex-1" />
        <Button type="button" size="sm" onClick={pickFile} disabled={uploading}>
          {uploading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Plus size={14} /> Upload {KIND_OPTIONS.find((o) => o.value === kind)?.label.toLowerCase()}
            </>
          )}
        </Button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic,.txt,.csv"
          onChange={onFile}
        />
      </div>

      {errMessage && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-brix-primary-tint">
          <AlertCircle size={14} className="text-brix-warning mt-0.5 shrink-0" />
          <p className="text-[11px] leading-relaxed text-brix-ink">{errMessage}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-brix-muted">
          <Loader2 size={14} className="animate-spin" /> Loading your documents…
        </div>
      ) : showEmpty ? (
        <p className="text-xs text-brix-muted leading-relaxed">
          No documents yet. Upload your signed contracts, W-9s, certificates of insurance,
          or anything you want a central copy of.
        </p>
      ) : (
        <ul className="divide-y divide-brix-border -my-2">
          {files.map((f) => (
            <li key={f.id} className="py-3 flex items-center gap-3">
              <KindIcon kind={f.kind} mime={f.mimeType} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brix-ink truncate" title={f.fileName}>
                  {f.fileName}
                </p>
                <p className="text-[11px] text-brix-muted truncate">
                  <KindChip kind={f.kind} />
                  <span className="mx-1.5">·</span>
                  {formatBytes(f.sizeBytes)}
                  <span className="mx-1.5">·</span>
                  {formatRelative(f.createdAt)}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onDownload(f)}
                disabled={downloadingId === f.id}
              >
                {downloadingId === f.id ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <Download size={14} /> Download
                  </>
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KindIcon({
  kind,
  mime,
}: {
  kind: CustomerFileKind;
  mime: string | null;
}) {
  const cls = 'text-brix-primary shrink-0';
  if (mime && mime.startsWith('image/')) return <FileImage size={20} className={cls} />;
  switch (kind) {
    case 'contract':
      return <FileSignature size={20} className={cls} />;
    case 'service_doc':
      return <Wrench size={20} className={cls} />;
    case 'invoice':
    case 'delivery_photo':
    case 'other':
    default:
      return mime === 'application/pdf' || mime === 'text/plain' ? (
        <FileText size={20} className={cls} />
      ) : (
        <FileIcon size={20} className={cls} />
      );
  }
}

function KindChip({ kind }: { kind: CustomerFileKind }) {
  const label =
    kind === 'contract'
      ? 'Contract'
      : kind === 'service_doc'
        ? 'Service doc'
        : kind === 'invoice'
          ? 'Invoice'
          : kind === 'delivery_photo'
            ? 'Delivery photo'
            : 'Other';
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] tracking-tight font-semibold bg-brix-surface-alt text-brix-primary border border-brix-border">
      {label}
    </span>
  );
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  if (diffMs < 60 * 1000) return 'just now';
  if (diffMs < 60 * 60 * 1000) {
    const m = Math.floor(diffMs / (60 * 1000));
    return `${m}m ago`;
  }
  if (diffMs < dayMs) {
    const h = Math.floor(diffMs / (60 * 60 * 1000));
    return `${h}h ago`;
  }
  if (diffMs < 7 * dayMs) {
    const days = Math.floor(diffMs / dayMs);
    return `${days}d ago`;
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
